import InfiniteScroll from "react-infinite-scroll-component";

import { formatConversationMessagesDate } from "../../utils";
import Message from "./Message/Message";

import type { Conversation, ConversationMessage } from "../../types";

interface MessageThreadProps {
  conversation: Conversation;
  setEditMessage: (message: ConversationMessage) => void;
  messages: ConversationMessage[];
  isLoadingMessages: boolean;
  loadNextMessages: () => void;
  hasMoreMessages: boolean;
  showLeadPreview: (leadId: string) => void;
}

export default function MessageThread({
  conversation,
  setEditMessage,
  messages,
  isLoadingMessages,
  loadNextMessages,
  hasMoreMessages,
  showLeadPreview,
}: MessageThreadProps) {
  const { owner: accountId, participants } = conversation;

  const commonMessageProps = {
    setEditMessage,
    participants: participants,
    showLeadPreview,
    accountId,
  };

  let messagesComponents = [];

  if (isLoadingMessages) {
    messagesComponents = new Array(6).fill({}).map((message, index) => (
      <Message
        // biome-ignore lint/suspicious/noArrayIndexKey: no unique key
        key={index}
        message={message}
        isLoading
        {...commonMessageProps}
      />
    ));
  } else if (messages) {
    for (let i = 0; i < messages.length; i += 1) {
      const message = messages[i];
      const date = new Date(message.created_at);

      messagesComponents.push(
        <Message
          key={message.urn + message.created_at}
          message={message}
          isLoading={false}
          {...commonMessageProps}
        />,
      );
      const nextDate = new Date(messages[i + 1]?.created_at);

      if (
        !nextDate ||
        date.getDate() !== nextDate.getDate() ||
        date.getMonth() !== nextDate.getMonth()
      ) {
        const formattedDate = formatConversationMessagesDate(date);

        messagesComponents.push(
          <div
            className="my-4 flex flex-row items-center justify-center gap-3"
            key={formattedDate}
          >
            <div className="h-px w-full bg-black-200" />

            <span className="text-nowrap text-button-xs text-black-700">
              {formattedDate}
            </span>

            <div className="h-px w-full bg-black-200" />
          </div>,
        );
      }
    }
  }

  return (
    <div
      id="thread-scroll-container"
      className="relative flex flex-1 flex-col-reverse overflow-y-auto p-4 scrollbar-thin"
    >
      <InfiniteScroll
        className="flex flex-col-reverse overflow-hidden pt-6 lg:pt-0"
        inverse
        next={loadNextMessages}
        hasMore={hasMoreMessages}
        dataLength={messagesComponents.length}
        loader={new Array(6).fill({}).map((message, index) => (
          <Message
            // biome-ignore lint/suspicious/noArrayIndexKey: no unique key
            key={index}
            message={message}
            isLoading
            {...commonMessageProps}
          />
        ))}
        scrollableTarget="thread-scroll-container"
      >
        {messagesComponents}
      </InfiniteScroll>
    </div>
  );
}
